import React, { useState } from 'react';

interface SearchFilters {
  keyword?: string;
  skill_ids?: number[];
  occupation_ids?: number[];
  industry_ids?: number[];
  characteristic_ids?: number[];
  prefecture_ids?: number[];
  workstyle_types?: string[];
  availability_types?: string[];
  price_over?: number;
  payment_site?: string;
  settlement_hours?: string;
  interview_count_max?: number;
  published_within?: number;
  is_recruiting?: boolean;
  remote_types?: string[];
  workday_types?: string[];
  job_source_ids?: number[];
  sort?: string;
  page?: number;
}

interface SaveSearchConditionModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchParams: SearchFilters;
  onSuccess?: () => void;
  isUserSignedIn?: boolean;
}

const SaveSearchConditionModal: React.FC<SaveSearchConditionModalProps> = ({
  isOpen,
  onClose,
  searchParams,
  onSuccess,
  isUserSignedIn = false
}) => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // メールアドレスのバリデーション
  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  // 検索パラメータをフィルタリングして、意味のある検索条件のみを抽出
  const filterSearchParams = (params: SearchFilters): SearchFilters => {
    const filtered: SearchFilters = {};

    // pageとsortを除外
    const excludeKeys = ['page', 'sort'];
    
    // 意味のある検索条件のみを抽出
    Object.entries(params).forEach(([key, value]) => {
      // pageとsortは除外
      if (excludeKeys.includes(key)) {
        return;
      }

      // 空の値や空の配列、falseの値を除外
      if (value === null || value === undefined) {
        return;
      }

      if (typeof value === 'string' && value.trim() === '') {
        return;
      }

      if (Array.isArray(value) && value.length === 0) {
        return;
      }

      if (value === false) {
        return;
      }

      // 意味のある値のみを追加
      filtered[key as keyof SearchFilters] = value;
    });

    return filtered;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);

    // 重複送信を防ぐ
    if (isSubmitting || isLoading) {
      return;
    }

    // バリデーション
    if (!email.trim()) {
      setErrors(['メールアドレスを入力してください']);
      return;
    }

    if (!validateEmail(email)) {
      setErrors(['正しいメールアドレスを入力してください']);
      return;
    }

    if (!agreedToTerms) {
      setErrors(['利用規約と個人情報の取り扱いに同意してください']);
      return;
    }

    setIsSubmitting(true);
    setIsLoading(true);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (!csrfToken) {
        throw new Error('CSRF token not found');
      }

      // 送信前に検索パラメータをフィルタリング
      const filteredSearchParams = filterSearchParams(searchParams);

      const response = await fetch('/guest_email_subscriptions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          email: email.trim(),
          search_params: filteredSearchParams,
          terms_agreed: agreedToTerms,
          privacy_policy_agreed: agreedToTerms
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Toastメッセージを表示
        if (window.showGlobalFlash) {
          window.showGlobalFlash(data.message || '新着案件の通知を受け取る設定を保存しました。新しい求人が追加された際にメールでお知らせします。', 'success', false);
        }

        // モーダルを閉じる
        setEmail('');
        setErrors([]);
        onClose();
        
        if (onSuccess) {
          onSuccess();
        }
      } else {
        let errorMessages: string[] = ['検索条件の保存に失敗しました'];
        
        try {
          const errorData = await response.json();
          console.error('[SaveSearchConditionModal] Error response:', errorData);
          
          if (errorData.errors && Array.isArray(errorData.errors)) {
            errorMessages = errorData.errors;
          } else if (errorData.error) {
            errorMessages = [errorData.error];
          } else if (errorData.message) {
            errorMessages = [errorData.message];
          }
        } catch (parseError) {
          console.error('[SaveSearchConditionModal] Failed to parse error response:', parseError);
          errorMessages = [`サーバーエラーが発生しました (ステータス: ${response.status})`];
        }
        
        setErrors(errorMessages);
      }
    } catch (error) {
      console.error('[SaveSearchConditionModal] Error saving search condition:', error);
      setErrors(['ネットワークエラーが発生しました。再度お試しください。']);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setErrors([]);
    setAgreedToTerms(false);
    onClose();
  };

  // 検索条件のテキストを生成（簡略版）
  const generateSearchConditionText = (): string => {
    const conditions: string[] = [];

    if (searchParams.keyword) {
      conditions.push(`キーワード: ${searchParams.keyword}`);
    }

    if (searchParams.skill_ids && searchParams.skill_ids.length > 0) {
      conditions.push(`スキル: ${searchParams.skill_ids.length}件`);
    }

    if (searchParams.occupation_ids && searchParams.occupation_ids.length > 0) {
      conditions.push(`職種: ${searchParams.occupation_ids.length}件`);
    }

    if (searchParams.prefecture_ids && searchParams.prefecture_ids.length > 0) {
      conditions.push(`都道府県: ${searchParams.prefecture_ids.length}件`);
    }

    if (searchParams.price_over) {
      conditions.push(`単価: ${(searchParams.price_over / 10000).toFixed(0)}万円以上`);
    }

    if (searchParams.remote_types && searchParams.remote_types.length > 0) {
      const remoteLabels = searchParams.remote_types.map(type => {
        switch (type) {
          case 'remote': return 'リモートOK';
          case 'full_remote': return 'フルリモート';
          case 'onsite': return '常駐';
          default: return type;
        }
      });
      conditions.push(`勤務形態: ${remoteLabels.join(', ')}`);
    }

    if (searchParams.is_recruiting) {
      conditions.push('募集中のみ');
    }

    return conditions.length > 0 ? conditions.join(' / ') : '検索条件が設定されています';
  };

  // 検索条件の詳細テキストを生成
  const generateDetailedSearchConditionText = (): string => {
    const conditions: string[] = [];

    if (searchParams.keyword) {
      conditions.push(`キーワード: ${searchParams.keyword}`);
    }

    if (searchParams.skill_ids && searchParams.skill_ids.length > 0) {
      conditions.push(`スキル: ${searchParams.skill_ids.length}件選択`);
    }

    if (searchParams.occupation_ids && searchParams.occupation_ids.length > 0) {
      conditions.push(`職種: ${searchParams.occupation_ids.length}件選択`);
    }

    if (searchParams.industry_ids && searchParams.industry_ids.length > 0) {
      conditions.push(`業界: ${searchParams.industry_ids.length}件選択`);
    }

    if (searchParams.characteristic_ids && searchParams.characteristic_ids.length > 0) {
      conditions.push(`特徴: ${searchParams.characteristic_ids.length}件選択`);
    }

    if (searchParams.prefecture_ids && searchParams.prefecture_ids.length > 0) {
      conditions.push(`都道府県: ${searchParams.prefecture_ids.length}件選択`);
    }

    if (searchParams.price_over) {
      conditions.push(`単価: ${(searchParams.price_over / 10000).toFixed(0)}万円以上`);
    }

    if (searchParams.remote_types && searchParams.remote_types.length > 0) {
      const remoteLabels = searchParams.remote_types.map(type => {
        switch (type) {
          case 'remote': return 'リモートOK';
          case 'full_remote': return 'フルリモート';
          case 'onsite': return '常駐';
          default: return type;
        }
      });
      conditions.push(`勤務形態: ${remoteLabels.join(', ')}`);
    }

    if (searchParams.workstyle_types && searchParams.workstyle_types.length > 0) {
      conditions.push(`働き方: ${searchParams.workstyle_types.length}件選択`);
    }

    if (searchParams.availability_types && searchParams.availability_types.length > 0) {
      conditions.push(`稼働可能時間: ${searchParams.availability_types.length}件選択`);
    }

    if (searchParams.workday_types && searchParams.workday_types.length > 0) {
      conditions.push(`週日数: ${searchParams.workday_types.length}件選択`);
    }

    if (searchParams.is_recruiting) {
      conditions.push('募集中のみ');
    }

    if (searchParams.job_source_ids && searchParams.job_source_ids.length > 0) {
      conditions.push(`求人サイト: ${searchParams.job_source_ids.length}件選択`);
    }

    return conditions.length > 0 ? conditions.join('、') : '検索条件が設定されていません';
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">新着案件を通知を受け取る</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
              disabled={isLoading}
            />
          </div>
          <div className="modal-body">
            <p className="mb-3">
              この検索条件で新着案件の通知を受け取ると、新しい求人が追加された際にメールでお知らせします。
            </p>

            {/* メールプレビュー */}
            <div className="mb-4 p-3 border rounded" style={{ backgroundColor: '#f8f9fa', fontSize: '14px', maxHeight: '500px', overflowY: 'auto' }}>
              <div style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.6', color: '#212529', margin: '0 auto', backgroundColor: '#f8f9fa' }}>
                <div style={{ width: '100%', maxWidth: '480px', padding: '8px 0', margin: '0 auto', backgroundColor: '#f8f9fa' }}>
                  
                  {/* Header */}
                  <div style={{ backgroundColor: '#ffffff', padding: '20px', textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#212529', marginBottom: '10px' }}>
                      インディバースフリーランス
                    </div>
                    <p style={{ marginTop: '20px', fontSize: '14px', color: '#212529', marginBottom: '0' }}>
                      保存していただいた検索条件に一致する新しい求人が追加されました。
                    </p>
                    
                    {/* 現在の検索条件の詳細表示 */}
                    <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px', textAlign: 'left' }}>
                      <p style={{ margin: '0', fontSize: '12px', fontWeight: 'bold', color: '#212529', marginBottom: '8px' }}>
                        【現在の検索条件】
                      </p>
                      <div style={{ fontSize: '12px', color: '#6c757d', lineHeight: '1.6' }}>
                        {generateDetailedSearchConditionText()}
                      </div>
                    </div>
                  </div>

                  {/* Job Listings */}
                  <div style={{ padding: '0 12px' }}>
                    <table border={0} align="center" cellPadding={0} cellSpacing={0} style={{ width: '100%', backgroundColor: '#ffffff', borderRadius: '8px' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px 0 3px', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', color: '#fff', background: 'linear-gradient(90deg, #00a67f 0%, #00bb94 50%, #00cc9f 100%)', borderRadius: '8px 8px 0 0' }}>
                            3件の新着案件
                          </td>
                        </tr>
                        
                        {/* サンプル案件1 */}
                        <tr>
                          <td style={{ padding: '0 15px' }}>
                            <div style={{ display: 'block', padding: '15px 0', textDecoration: 'none', backgroundColor: '#ffffff', borderBottom: '1px solid #dee2e6' }}>
                              <p style={{ margin: '0', fontSize: '16px', fontWeight: 'bold', color: '#212529' }}>
                                <span style={{ display: 'inline-block', boxSizing: 'border-box', borderRadius: '6px', padding: '6px 6px', color: '#ffffff', fontSize: '11px', lineHeight: '1', wordBreak: 'keep-all', backgroundColor: '#ff8800', marginRight: '4px' }}>NEW</span>
                                <span style={{ color: '#212529' }}>【Ruby/Rails】Webアプリケーション開発案件</span>
                              </p>
                              
                              <ul style={{ color: '#212529', padding: '0', margin: '8px 0', listStyle: 'none' }}>
                                <li style={{ display: 'inline-block', fontSize: '10px', color: '#6c757d', wordBreak: 'break-word', marginRight: '8px' }}>
                                  📍 リモート
                                </li>
                                <li style={{ fontSize: '14px', color: '#212529', margin: '4px 0' }}>
                                  💰 <strong style={{ color: '#e72350', fontWeight: 'bold' }}>60〜80</strong>万円/月
                                </li>
                                <li style={{ fontSize: '14px', color: '#212529', margin: '4px 0' }}>
                                  📍 東京都
                                </li>
                              </ul>
                              
                              <p style={{ margin: '0', fontSize: '10px', color: '#6c757d' }}>
                                スタートアップ企業のWebアプリケーション開発を担当していただきます。Ruby on Railsを使用した...
                              </p>
                              
                              <div style={{ maxWidth: '300px', padding: '12px 0', margin: '8px auto 0', fontSize: '14px', fontWeight: 'bold', color: '#ffffff', textAlign: 'center', backgroundColor: '#00bb94', border: '1px solid #00bb94', borderRadius: '8px' }}>
                                案件を見る →
                              </div>
                            </div>
                          </td>
                        </tr>

                        {/* サンプル案件2 */}
                        <tr>
                          <td style={{ padding: '0 15px' }}>
                            <div style={{ display: 'block', padding: '15px 0', textDecoration: 'none', backgroundColor: '#ffffff', borderBottom: '1px solid #dee2e6' }}>
                              <p style={{ margin: '0', fontSize: '16px', fontWeight: 'bold', color: '#212529' }}>
                                <span style={{ display: 'inline-block', boxSizing: 'border-box', borderRadius: '6px', padding: '6px 6px', color: '#ffffff', fontSize: '11px', lineHeight: '1', wordBreak: 'keep-all', backgroundColor: '#ff8800', marginRight: '4px' }}>NEW</span>
                                <span style={{ color: '#212529' }}>【React/TypeScript】フロントエンド開発案件</span>
                              </p>
                              
                              <ul style={{ color: '#212529', padding: '0', margin: '8px 0', listStyle: 'none' }}>
                                <li style={{ display: 'inline-block', fontSize: '10px', color: '#6c757d', wordBreak: 'break-word', marginRight: '8px' }}>
                                  📍 フルリモート
                                </li>
                                <li style={{ fontSize: '14px', color: '#212529', margin: '4px 0' }}>
                                  💰 <strong style={{ color: '#e72350', fontWeight: 'bold' }}>70〜90</strong>万円/月
                                </li>
                                <li style={{ fontSize: '14px', color: '#212529', margin: '4px 0' }}>
                                  📍 リモート
                                </li>
                              </ul>
                              
                              <p style={{ margin: '0', fontSize: '10px', color: '#6c757d' }}>
                                ReactとTypeScriptを使用したモダンなフロントエンド開発。コンポーネント設計から実装まで...
                              </p>
                              
                              <div style={{ maxWidth: '300px', padding: '12px 0', margin: '8px auto 0', fontSize: '14px', fontWeight: 'bold', color: '#ffffff', textAlign: 'center', backgroundColor: '#00bb94', border: '1px solid #00bb94', borderRadius: '8px' }}>
                                案件を見る →
                              </div>
                            </div>
                          </td>
                        </tr>

                        {/* サンプル案件3 */}
                        <tr>
                          <td style={{ padding: '0 15px' }}>
                            <div style={{ display: 'block', padding: '15px 0', textDecoration: 'none', backgroundColor: '#ffffff' }}>
                              <p style={{ margin: '0', fontSize: '16px', fontWeight: 'bold', color: '#212529' }}>
                                <span style={{ display: 'inline-block', boxSizing: 'border-box', borderRadius: '6px', padding: '6px 6px', color: '#ffffff', fontSize: '11px', lineHeight: '1', wordBreak: 'keep-all', backgroundColor: '#ff8800', marginRight: '4px' }}>NEW</span>
                                <span style={{ color: '#212529' }}>【Python/Django】バックエンドAPI開発案件</span>
                              </p>
                              
                              <ul style={{ color: '#212529', padding: '0', margin: '8px 0', listStyle: 'none' }}>
                                <li style={{ fontSize: '14px', color: '#212529', margin: '4px 0' }}>
                                  💰 <strong style={{ color: '#e72350', fontWeight: 'bold' }}>65〜85</strong>万円/月
                                </li>
                                <li style={{ fontSize: '14px', color: '#212529', margin: '4px 0' }}>
                                  📍 東京都
                                </li>
                                <li style={{ fontSize: '14px', color: '#212529', margin: '4px 0' }}>
                                  🚃 新宿駅
                                </li>
                              </ul>
                              
                              <p style={{ margin: '0', fontSize: '10px', color: '#6c757d' }}>
                                Djangoを使用したRESTful APIの設計・開発。データベース設計から実装まで...
                              </p>
                              
                              <div style={{ maxWidth: '300px', padding: '12px 0', margin: '8px auto 0', fontSize: '14px', fontWeight: 'bold', color: '#ffffff', textAlign: 'center', backgroundColor: '#00bb94', border: '1px solid #00bb94', borderRadius: '8px' }}>
                                案件を見る →
                              </div>
                            </div>
                          </td>
                        </tr>
                        
                        {/* See More */}
                        <tr>
                          <td style={{ padding: '24px 15px', textAlign: 'center' }}>
                            <p style={{ margin: '0', fontSize: '14px', textAlign: 'center' }}>＼最新案件情報を毎日更新／</p>
                            <div style={{ display: 'inline-block', width: '100%', maxWidth: '300px', padding: '14px 0', boxSizing: 'border-box', fontSize: '16px', fontWeight: 'bold', color: '#00bb94', backgroundColor: '#ffffff', border: '1px solid #00bb94', borderRadius: '8px', marginTop: '12px' }}>
                              最新案件をもっと見る
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Footer */}
                  <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                    <p style={{ margin: '0 0 8px 0' }}>
                      <a href="#" style={{ color: '#00bb94', textDecoration: 'underline' }}>配信停止</a>
                    </p>
                    <p style={{ margin: '0' }}>
                      株式会社インディバース<br />
                      インディバースフリーランス運営事務局
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* 会員向けメッセージ */}
              {isUserSignedIn && (
                <div className="alert alert-info mb-3" role="alert">
                  <small>会員の方はログイン後にご利用ください。</small>
                </div>
              )}

              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  メールアドレス
                </label>
                <input
                  type="email"
                  className={`form-control ${errors.length > 0 ? 'is-invalid' : ''}`}
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@example.com"
                  disabled={isLoading}
                  required
                />
                {errors.length > 0 && (
                  <div className="invalid-feedback">
                    {errors.map((error, index) => (
                      <div key={index}>{error}</div>
                    ))}
                  </div>
                )}
              </div>

              {/* 利用規約と個人情報の取り扱いに同意 */}
              <div className="mb-3">
                <div className="d-flex justify-content-end align-items-center">
                  <label 
                    className="text-end me-2 mb-0" 
                    htmlFor="agreeToTerms" 
                    style={{ textAlign: 'right', lineHeight: '1.5', cursor: 'pointer' }}
                    onClick={() => !isLoading && setAgreedToTerms(!agreedToTerms)}
                  >
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{ color: '#00bb94' }}>利用規約</a>と
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{ color: '#00bb94' }}>個人情報の取り扱い</a>に同意してメールを受け取る（無料）
                  </label>
                  <i 
                    className={`bi ${agreedToTerms ? 'bi-toggle-on' : 'bi-toggle-off'}`}
                    style={{ 
                      fontSize: '32px', 
                      color: agreedToTerms ? '#00bb94' : '#adb5bd',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      flexShrink: 0
                    }}
                    onClick={() => !isLoading && setAgreedToTerms(!agreedToTerms)}
                  ></i>
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    disabled={isLoading}
                    style={{ display: 'none' }}
                  />
                </div>
                {errors.some(e => e.includes('利用規約')) && (
                  <div className="invalid-feedback d-block">
                    {errors.find(e => e.includes('利用規約'))}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      保存中...
                    </>
                  ) : (
                    '保存する'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveSearchConditionModal;

